import { jsx } from 'react/jsx-runtime';
import { createContext, useEffect, useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LOCAL_FEATURE_FLAGS } from './feature-flags-helper.js';
import { getFeatureFlagOverridesFromEnv, getURLParamsFeatureFlagOverrides } from './feature-flag-overrides.js';
import { DEFAULT_CLIENT_FEATURE_FLAGS, mapFeatureFlagResponse, FEATURE_FLAG_QUERY_VARIABLES } from './feature-flags-utils.js';
import { logError } from '../errors/error-reporting.js';
import { gql } from 'urql';
import { useQuery } from '../graphql/graphql.js';
import '../authentication/AuthenticationContextProvider.js';
import { useIsAuthenticated, useIsInternalUser } from '../authentication/permissions.js';
import { GetFeatureFlagsV2Context } from '../generated/graphql.js';
import { useLocalStorage } from '../browser-storage/browser-storage.js';

/**
 * Default values for all feature flags, both from the GQL API and local feature flags.
 */ const DEFAULT_FEATURE_FLAGS = {
    ...DEFAULT_CLIENT_FEATURE_FLAGS,
    ...LOCAL_FEATURE_FLAGS
};
const GET_FEATURE_FLAGS = gql`
  query GetFeatureFlagsV2($flags: [FeatureFlagRequest!]!) {
    featureFlagsV2(flags: $flags) {
      key
      value {
        ... on BoolValue {
          boolValue: value
        }
        ... on StringValue {
          stringValue: value
        }
        ... on IntValue {
          intValue: value
        }
      }
    }
  }
`;
/**
 * @internal This should not be used directly, use the `FeatureFlagContextProvider` instead
 */ const FeatureFlagContext = /*#__PURE__*/ createContext({
    isLoading: false,
    originalFeatureFlags: DEFAULT_FEATURE_FLAGS,
    featureFlags: DEFAULT_FEATURE_FLAGS,
    setFeatureFlags: ()=>{
    // noop
    },
    reset: ()=>{
    // noop
    }
});
/**
 * Context provider that loads feature flag state from the GQL API, combines it with any local flag state, and makes
 * the flags available to the rest of the application.
 */ function FeatureFlagContextProvider(props) {
    const isAuthenticated = useIsAuthenticated();
    const [searchParams] = useSearchParams();
    const [{ data, error, fetching }] = useQuery({
        query: GET_FEATURE_FLAGS,
        context: GetFeatureFlagsV2Context,
        variables: FEATURE_FLAG_QUERY_VARIABLES,
        pause: !isAuthenticated
    });
    // Since we "handle" this error instead of throwing it, make sure it gets reported
    useEffect(()=>{
        if (error === undefined || error === null) return;
        logError(error);
    }, [
        error
    ]);
    const flags = {
        ...mapFeatureFlagResponse(data),
        ...LOCAL_FEATURE_FLAGS
    };
    const isChronoAdmin = useIsInternalUser();
    const envConfigLocalOverrides = getFeatureFlagOverridesFromEnv();
    const [localStorageFeatureFlags, setLocalStorageFeatureFlags] = useLocalStorage('featureFlagsOverrides', {});
    // This hook is responsible for checking if there are any URL params that override the feature flags
    useEffect(()=>{
        const paramOverrides = getURLParamsFeatureFlagOverrides(searchParams.toString());
        // If there are URL param overrides, we need to update the local storage overrides with them
        if (Object.keys(paramOverrides).length > 0) {
            // Confirm with the user if they have existing local storage overrides
            if (Object.keys(localStorageFeatureFlags).length > 0) {
                const confirmed = window.confirm('You have opened a link with URL feature flag overrides but you have existing overrides set. Do you want to apply the overrides from the URL? Any existing overrides will be overwritten.');
                if (!confirmed) {
                    return;
                }
            }
            setLocalStorageFeatureFlags(paramOverrides);
        }
    // Only do this once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Reset the local storage overrides to original state
    const reset = useCallback(()=>{
        setLocalStorageFeatureFlags({}); // Clear the local storage overrides
    }, [
        setLocalStorageFeatureFlags
    ]);
    // Only merge the flags if the user is a Chrono Admin
    const mergedFlags = isChronoAdmin ? {
        ...flags,
        ...envConfigLocalOverrides,
        ...localStorageFeatureFlags
    } : flags;
    return /*#__PURE__*/ jsx(FeatureFlagContext.Provider, {
        value: {
            isLoading: fetching,
            originalFeatureFlags: flags,
            featureFlags: mergedFlags,
            setFeatureFlags: setLocalStorageFeatureFlags,
            reset
        },
        children: props.children
    });
}
function useFeatureFlagsState() {
    const { featureFlags, setFeatureFlags, originalFeatureFlags, reset } = useContext(FeatureFlagContext);
    return {
        originalFeatureFlags,
        featureFlags,
        setFeatureFlags,
        reset
    };
}
function useFeatureFlags() {
    const { featureFlags } = useFeatureFlagsState();
    return featureFlags;
}
function useIsFeatureFlagsLoading() {
    const featureFlagsContext = useContext(FeatureFlagContext);
    return featureFlagsContext.isLoading;
}
/**
 * A component that conditionally renders its children based on the provided condition and the current feature flags.
 */ function FeatureFlagGate(props) {
    const { condition, fallback, children } = props;
    const { featureFlags } = useFeatureFlagsState();
    const isAllowed = condition({
        featureFlags
    });
    if (!isAllowed) return fallback;
    return children;
}

export { DEFAULT_FEATURE_FLAGS, FeatureFlagContext, FeatureFlagContextProvider, FeatureFlagGate, useFeatureFlags, useFeatureFlagsState, useIsFeatureFlagsLoading };
//# sourceMappingURL=FeatureFlags.js.map
